import { lightenHex, type PresencePalette, rgba } from "./palette";

/**
 * Orbul: bijuteria lichidă de la baza paginii. Portat din
 * `docs/prototypes/presence.html`.
 *
 * Morful orb→card a fost scos la primul port, fiindcă panoul clasic rămânea
 * neatins (vezi `widget.ts`) și modul de voce cerea doar orbul. S-a întors
 * odată cu orb-chatul (`orbchat/`, sub `?aig_orb_chat=1`), dar ca funcții
 * SEPARATE - `traceMorph` / `morphGeometry` / `drawMorph`. `drawOrb` de mai jos
 * e neschimbat, fiindcă e ce desenează calea clasică: o singură ramură comună
 * ar fi însemnat că fiecare reglaj al cardului trece prin randorul pe care îl
 * văd toate site-urile de azi.
 *
 * Toate funcțiile sunt pure în raport cu starea - primesc timpul, energia și
 * paleta, și nu rețin nimic între cadre în afară de unghiurile particulelor.
 * Așa bucla de desen poate fi oprită și repornită fără să sară nimic.
 */

/** Câte puncte pe contur. 96 e pragul sub care armonicile devin vizibil poligonale. */
const CONTOUR_STEPS = 96;

export interface Particle {
  /** Unghiul curent pe orbita din interiorul orbului. */
  a: number;
  /** Raza orbitei, ca fracție din raza orbului. */
  r: number;
  /** Viteza unghiulară, cu semn (unele se învârt invers). */
  sp: number;
  /** Mărimea punctului, în px. */
  sz: number;
  /** Faza proprie, ca să nu clipească toate odată. */
  ph: number;
}

/**
 * Șase particule, nu douăzeci: prototipul le-a redus tocmai fiindcă un roi des
 * citea a „încărcare", nu a prezență. Aleatoriul e la construcție, o singură
 * dată - de-aia primește un generator, ca testele să-l poată face determinist.
 */
export function createParticles(rand: () => number = Math.random): Particle[] {
  return Array.from({ length: 6 }, () => ({
    a: rand() * Math.PI * 2,
    r: 0.18 + rand() * 0.66,
    sp: (0.12 + rand() * 0.25) * (rand() < 0.5 ? -1 : 1),
    sz: 0.6 + rand() * 1,
    ph: rand() * Math.PI * 2,
  }));
}

/**
 * Armonicile lichide din brand kit. Cele trei frecvențe (k=2,3,5) au viteze
 * incomensurabile între ele, deci conturul nu se repetă niciodată identic - de
 * asta forma pare vie și nu buclată.
 */
function harmonics(theta: number, ts: number): number {
  return (
    0.5 * Math.sin(2 * theta - 0.21 * ts + 0.7) +
    0.33 * Math.sin(3 * theta + 0.34 * ts + 2.1) +
    0.22 * Math.sin(5 * theta - 0.53 * ts + 4.4)
  );
}

/** Conturul: cerc cu armonici pe margine, trasat ca path închis. */
export function traceBlob(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  ts: number,
  amp: number,
): void {
  const rot = 0.02 * ts;
  ctx.beginPath();
  for (let i = 0; i <= CONTOUR_STEPS; i++) {
    const theta = (i / CONTOUR_STEPS) * Math.PI * 2 + rot;
    const d = 1 + amp * harmonics(theta, ts);
    const x = cx + Math.cos(theta) * radius * d;
    const y = cy + Math.sin(theta) * radius * d;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export interface OrbFrame {
  palette: PresencePalette;
  /** Centrul orbului în coordonate CSS ale canvasului. */
  cx: number;
  cy: number;
  /** Raza în repaus, în px. */
  baseRadius: number;
  /** Energia 0..1 din `EnergyEnvelope`. */
  energy: number;
  /** Faza respirației, avansată de buclă. */
  breath: number;
  /** Timpul în secunde - conduce armonicile și orbitele. */
  ts: number;
  /** Delta cadrului, în ms - avansează particulele. */
  dtMs: number;
  /** 0 = orb stins (repaus, widget închis), 1 = complet prezent. */
  presence: number;
  /** Cât de „concentrat" e - 1 în `processing`. Accelerează particulele. */
  focus: number;
  particles: Particle[];
}

/** Raza efectivă a orbului în cadrul curent - și waveform-ul are nevoie de ea. */
export function orbRadius(baseRadius: number, breath: number, energy: number, ts: number): number {
  // Respirație de 2%: prototipul a coborât-o deliberat de la o valoare mai mare,
  // fiindcă orbul stă pe site-ul altcuiva și nu are voie să atragă atenția în repaus.
  const depth = 0.02 * (1 + 0.2 * Math.sin(0.133 * ts));
  return baseRadius * (1 + depth * Math.sin(breath) + 0.1 * energy);
}

export function drawOrb(ctx: CanvasRenderingContext2D, f: OrbFrame): number {
  const { palette: p, cx, cy, energy, ts } = f;
  const radius = orbRadius(f.baseRadius, f.breath, energy, ts);
  const alpha = 0.5 + 0.5 * f.presence;
  // Bugetul de mișcare al conturului: abia lichid. Peste ~0.09 forma începe să
  // pară o amibă, ceea ce citește a jucărie, nu a instrument.
  const blobAmp = 0.022 + 0.045 * energy;

  ctx.globalAlpha = alpha;

  // Halo - lumina care iese din formă. Desenat primul, ca tot restul să stea peste.
  const halo = ctx.createRadialGradient(cx, cy, radius * 0.6, cx, cy, radius * 1.85);
  halo.addColorStop(0, rgba(p.glow, 0.27));
  halo.addColorStop(0.6, rgba(p.glow, 0.08));
  halo.addColorStop(1, rgba(p.glow, 0));
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 2.7, 0, Math.PI * 2);
  ctx.fill();

  // Corpul: gradient radial cu punctul de lumină care rătăcește lent, ca
  // reflexia de pe o sferă de sticlă.
  const hx = cx + radius * 0.32 * Math.cos(0.11 * ts + 2);
  const hy = cy - radius * 0.35 + radius * 0.14 * Math.sin(0.07 * ts);
  const body = ctx.createRadialGradient(hx, hy, radius * 0.1, cx, cy, radius);
  body.addColorStop(0, rgba(p.core, 0.95));
  body.addColorStop(0.55, rgba(p.glow, 0.6));
  body.addColorStop(1, rgba(p.deep, 0.5));
  ctx.fillStyle = body;
  traceBlob(ctx, cx, cy, radius, ts, blobAmp);
  ctx.fill();

  // Particulele, tăiate pe conturul orbului ca să nu evadeze.
  ctx.save();
  traceBlob(ctx, cx, cy, radius * 0.94, ts, blobAmp);
  ctx.clip();
  const spin = 1 + 2.2 * f.focus;
  for (const part of f.particles) {
    part.a += (part.sp * spin * f.dtMs) / 1000;
    const sway = 0.9 + 0.1 * Math.sin(ts * 1.43 + part.ph);
    const px = cx + Math.cos(part.a) * part.r * radius * sway;
    const py = cy + Math.sin(part.a) * part.r * radius * 0.92 * sway;
    ctx.fillStyle = rgba("#ffffff", 0.22 + 0.4 * Math.abs(Math.sin(ts * 1.67 + part.ph)));
    ctx.beginPath();
    ctx.arc(px, py, part.sz * (0.8 + 0.4 * energy), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // Inelul-menisc: marginea de tensiune superficială. Respiră cu orbul.
  ctx.strokeStyle = rgba(p.core, 0.45);
  ctx.lineWidth = 1;
  traceBlob(ctx, cx, cy, radius + 2.5 + 0.6 * Math.sin(f.breath), ts, blobAmp * 0.7);
  ctx.stroke();

  ctx.globalAlpha = 1;
  return radius;
}

/* ============================== inelul de HUD ============================== */

/** Câte repere pe cercul de ticks. 24 = unul la 15°, adică un cadran citibil. */
const HUD_TICKS = 24;
/** Lungimea unui tick, în px. Fix, nu proporțional: e o gradație, nu o formă. */
const HUD_TICK_LEN = 2;
/** Unde începe cercul de ticks, ca multiplu al razei orbului. */
const HUD_TICK_RADIUS_MULT = 1.55;
/** Raza arcurilor exterioare (două, opuse) și a celui interior. */
const HUD_OUTER_RADIUS_MULT = 1.38;
const HUD_INNER_RADIUS_MULT = 1.22;
/** Cât ține o rotație completă, în secunde. Exterior lent, interior mai iute. */
const HUD_OUTER_PERIOD_S = 32;
const HUD_INNER_PERIOD_S = 13;
/**
 * Peste ce energie primesc arcurile glow. Sub prag - adică în tot repausul -
 * inelul e desen curat, fără nicio aură: widgetul stă pe situl altcuiva și n-are
 * voie să fie atracția paginii. Lumina apare doar când chiar se vorbește.
 */
const HUD_GLOW_ENERGY_MIN = 0.4;
/** Deschiderea arcurilor, în radiani. */
const HUD_OUTER_SWEEP = (70 * Math.PI) / 180;
const HUD_INNER_SWEEP = (110 * Math.PI) / 180;

/**
 * Inelul de instrument din jurul orbului - pielea `hud` (aprinsă doar de orb-chat).
 *
 * Deliberat MECANIC: unghiurile cresc liniar cu timpul, fără nicio ușurare și
 * fără respirație. Orbul e materia vie; inelul e aparatura din jurul lui, iar
 * dacă ar și el respira, cele două s-ar citi ca un singur obiect moale. De-aia
 * e o funcție separată de `drawOrb`, nu un parametru al lui: calea clasică
 * desenează exact ce desena înainte, bit cu bit.
 *
 * Pură în raport cu starea, ca tot restul fișierului: primește timpul și
 * energia, nu reține nimic între cadre.
 */
export function drawHudRing(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  ts: number,
  energy: number,
  palette: PresencePalette,
): void {
  const e = Math.max(0, Math.min(1, energy));
  ctx.save();

  // Gradația: 24 de repere scurte, la fel de aprinse între ele. Ele dau senzația
  // de instrument calibrat; arcurile de deasupra dau mișcarea.
  ctx.strokeStyle = rgba("#ffffff", 0.1 + 0.25 * e);
  ctx.lineWidth = 1;
  const tickFrom = radius * HUD_TICK_RADIUS_MULT;
  const tickTo = tickFrom + HUD_TICK_LEN;
  ctx.beginPath();
  for (let i = 0; i < HUD_TICKS; i++) {
    const theta = (i / HUD_TICKS) * Math.PI * 2;
    const co = Math.cos(theta);
    const si = Math.sin(theta);
    ctx.moveTo(cx + co * tickFrom, cy + si * tickFrom);
    ctx.lineTo(cx + co * tickTo, cy + si * tickTo);
  }
  ctx.stroke();

  // Doar arcurile primesc glow, și doar când chiar se vorbește (vezi
  // `HUD_GLOW_ENERGY_MIN`): pe reperele de 2px s-ar transforma oricum în ceață.
  if (e > HUD_GLOW_ENERGY_MIN) {
    ctx.shadowBlur = 5 * e;
    ctx.shadowColor = rgba(palette.glow, 0.5);
  }

  // Două segmente opuse, în sensul acelor de ceasornic.
  const outer = ((ts % HUD_OUTER_PERIOD_S) / HUD_OUTER_PERIOD_S) * Math.PI * 2;
  // Trase cu `core`, nu cu `glow`: pe un fundal saturat glow-ul mediu cade
  // sub ~2:1 și arcul dispare - fața luminoasă a metalului rămâne vizibilă
  // oriunde. Glow-ul rămâne doar în aură (shadowColor), unde difuzia e scopul.
  ctx.strokeStyle = rgba(palette.core, 0.28 + 0.37 * e);
  ctx.lineWidth = 1.25;
  for (const offset of [0, Math.PI]) {
    const start = outer + offset;
    ctx.beginPath();
    ctx.arc(cx, cy, radius * HUD_OUTER_RADIUS_MULT, start, start + HUD_OUTER_SWEEP);
    ctx.stroke();
  }

  // Unul singur, mai aproape și în sens invers: contra-rotația e ce face inelul
  // să pară două piese, nu un desen care se învârte.
  const inner = -((ts % HUD_INNER_PERIOD_S) / HUD_INNER_PERIOD_S) * Math.PI * 2;
  // Mereu mai stins decât perechea exterioară: două arcuri la fel de aprinse ar
  // citi ca un singur inel gros.
  ctx.strokeStyle = rgba(palette.core, 0.2 + 0.3 * e);
  ctx.beginPath();
  ctx.arc(cx, cy, radius * HUD_INNER_RADIUS_MULT, inner, inner + HUD_INNER_SWEEP);
  ctx.stroke();

  ctx.restore();
}

/* ============================ morful orb → card ============================ */

/**
 * Conturul morfat: superelipsă interpolată cerc→dreptunghi rotunjit, cu
 * aceleași armonici pe margine ca orbul. `n` e exponentul superelipsei: 2 =
 * cerc/elipsă, ~4.8 = dreptunghi cu colțuri încă rotunde (v2 din prototip;
 * peste 6 forma devine o cutie și pierde legătura cu orbul din care a ieșit).
 *
 * Rotația conturului se STINGE pe măsură ce forma devine card: un dreptunghi
 * care se rotește, oricât de lent, citește a defect, nu a materie vie.
 */
export function traceMorph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  a: number,
  b: number,
  n: number,
  ts: number,
  amp: number,
): void {
  const rot = 0.02 * ts * (1 - Math.min(1, (n - 2) / 4));
  ctx.beginPath();
  for (let i = 0; i <= CONTOUR_STEPS; i++) {
    const theta = (i / CONTOUR_STEPS) * Math.PI * 2 + rot;
    const co = Math.abs(Math.cos(theta));
    const si = Math.abs(Math.sin(theta));
    const r = Math.pow(Math.pow(co / a, n) + Math.pow(si / b, n), -1 / n);
    const d = 1 + amp * harmonics(theta, ts);
    const x = cx + Math.cos(theta) * r * d;
    const y = cy + Math.sin(theta) * r * d;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export interface MorphInput {
  /** 0 = orb, 1 = card. Interpolat de buclă, nu sărit. */
  m: number;
  /** Raza orbului în cadrul curent (`orbRadius`). */
  radius: number;
  /** Energia 0..1 - doar ea mai mișcă conturul când forma e card. */
  energy: number;
  /** Centrul orbului pe verticală, în coordonatele canvasului. */
  orbCy: number;
  cardWidth: number;
  cardHeight: number;
  /** Centrul cardului pe verticală, în coordonatele canvasului. */
  cardCy: number;
}

export interface MorphGeometry {
  /** Semiaxa orizontală. */
  a: number;
  /** Semiaxa verticală. */
  b: number;
  /** Centrul interpolat pe verticală. */
  cy: number;
  /** Exponentul superelipsei. */
  n: number;
  /** Amplitudinea armonicilor de contur. */
  blobAmp: number;
}

/**
 * Geometria interpolată, pură - de-aia stă separat de desen: e singura parte a
 * morfului care se poate verifica exact (m=0 trebuie să dea ORBUL, m=1 cardul,
 * iar între ele nimic nu are voie să sară).
 *
 * Bugetul de mișcare al conturului scade cu 92% pe card: orbul e abia lichid,
 * cardul e practic geometrie precisă. Un card care unduie e o jucărie.
 */
export function morphGeometry(input: MorphInput): MorphGeometry {
  const { m, radius } = input;
  return {
    a: radius + (input.cardWidth / 2 - radius) * m,
    b: radius + (input.cardHeight / 2 - radius) * m,
    cy: input.orbCy + (input.cardCy - input.orbCy) * m,
    n: 2 + 2.8 * m,
    blobAmp: (0.022 + 0.045 * input.energy) * (1 - 0.92 * m),
  };
}

export interface MorphFrame extends OrbFrame {
  /** 0 = orb, 1 = card. */
  morph: number;
  cardWidth: number;
  cardHeight: number;
  /** Centrul cardului pe verticală, în coordonatele canvasului. */
  cardCy: number;
}

/**
 * Orbul ȘI cardul, în aceeași materie. Întoarce semiaxa orizontală, ca
 * waveform-ul să pornească de la marginea formei CURENTE, nu de la orb.
 *
 * Regula de culoare a lui v2, care e tot ce deosebește cardul ăsta de o casetă
 * de chat obișnuită: culoarea plină trăiește doar în LUMINI (halo, orb,
 * particule); suprafața cardului e sticlă mată, amestecată spre alb din paletă.
 */
export function drawMorph(ctx: CanvasRenderingContext2D, f: MorphFrame): number {
  const { palette: p, cx, energy, ts } = f;
  const m = Math.max(0, Math.min(1, f.morph));
  const radius = orbRadius(f.baseRadius, f.breath, energy, ts);
  const g = morphGeometry({
    m,
    radius,
    energy,
    orbCy: f.cy,
    cardWidth: f.cardWidth,
    cardHeight: f.cardHeight,
    cardCy: f.cardCy,
  });
  const alpha = 0.5 + 0.5 * f.presence;
  ctx.globalAlpha = alpha;

  // Halo: se stinge pe măsură ce apare cardul, care are propria umbră.
  if (m < 0.85) {
    ctx.globalAlpha = (1 - m) * alpha;
    const halo = ctx.createRadialGradient(cx, f.cy, radius * 0.6, cx, f.cy, radius * 1.85);
    halo.addColorStop(0, rgba(p.glow, 0.27));
    halo.addColorStop(0.6, rgba(p.glow, 0.08));
    halo.addColorStop(1, rgba(p.glow, 0));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, f.cy, radius * 2.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = alpha;
  }

  ctx.save();
  if (m > 0.05) {
    // Umbra de podea apare odată cu cardul: fără ea sticla mată plutește peste
    // pagina clientului fără să atingă nimic.
    ctx.shadowColor = `rgba(10,6,24,${0.4 * m})`;
    ctx.shadowBlur = 26 * m;
    ctx.shadowOffsetY = 10 * m;
  }
  let fill: CanvasGradient;
  if (m < 0.5) {
    const hx = cx + g.a * 0.32 * Math.cos(0.11 * ts + 2);
    const hy = g.cy - g.b * 0.35 + g.b * 0.14 * Math.sin(0.07 * ts);
    fill = ctx.createRadialGradient(hx, hy, Math.min(g.a, g.b) * 0.1, cx, g.cy, Math.max(g.a, g.b));
    fill.addColorStop(0, rgba(p.core, 0.95));
    fill.addColorStop(0.55, rgba(p.glow, 0.6));
    fill.addColorStop(1, rgba(p.deep, 0.5));
  } else {
    // Calm: gradient vertical, fără niciun highlight care se plimbă. Sticla nu
    // are voie să atragă atenția de pe cuvinte.
    fill = ctx.createLinearGradient(0, g.cy - g.b, 0, g.cy + g.b);
    fill.addColorStop(0, lightenHex(p.core, 0.93));
    fill.addColorStop(0.6, lightenHex(p.core, 0.88));
    fill.addColorStop(1, lightenHex(p.glow, 0.85));
  }
  ctx.globalAlpha = alpha * (1 - 0.1 * m);
  ctx.fillStyle = fill;
  traceMorph(ctx, cx, g.cy, g.a, g.b, g.n, ts, g.blobAmp);
  ctx.fill();
  ctx.restore();
  ctx.globalAlpha = alpha;

  // Gândirea: sticla se colorează ușor spre lumină. Pe alb NU adâncim spre
  // închis - ar deveni o altă suprafață, nu aceeași sticlă concentrată.
  if (m > 0.05 && f.focus > 0.02) {
    ctx.fillStyle = rgba(p.glow, 0.07 * m * f.focus);
    traceMorph(ctx, cx, g.cy, g.a, g.b, g.n, ts, g.blobAmp);
    ctx.fill();
  }

  // Particulele: vii în orb, o șoaptă pe card.
  ctx.save();
  traceMorph(ctx, cx, g.cy, g.a * 0.94, g.b * 0.94, g.n, ts, g.blobAmp);
  ctx.clip();
  const spin = 1 + 2.2 * f.focus;
  const partAlpha = 1 - 0.8 * m;
  for (const part of f.particles) {
    part.a += (part.sp * spin * f.dtMs) / 1000;
    const sway = 0.9 + 0.1 * Math.sin(ts * 1.43 + part.ph);
    const px = cx + Math.cos(part.a) * part.r * g.a * sway;
    const py = g.cy + Math.sin(part.a) * part.r * g.b * 0.92 * sway;
    const flicker = Math.abs(Math.sin(ts * 1.67 + part.ph));
    ctx.fillStyle =
      m < 0.5
        ? rgba("#ffffff", (0.22 + 0.4 * flicker) * partAlpha)
        : rgba(p.glow, (0.1 + 0.14 * flicker) * partAlpha + 0.06);
    ctx.beginPath();
    ctx.arc(px, py, part.sz * (0.8 + 0.4 * energy) * (1 - 0.3 * m), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // Inelul-menisc devine hairline pe card: sticlă tăiată fin, nu chenar.
  const pulse = Math.sin(f.breath);
  ctx.strokeStyle = rgba(m > 0.5 ? p.glow : p.core, m > 0.5 ? 0.32 : 0.45);
  ctx.lineWidth = 1;
  traceMorph(
    ctx,
    cx,
    g.cy,
    g.a + 2.5 + 0.6 * pulse * (1 - m),
    g.b + 2.5 + 0.6 * pulse * (1 - m),
    g.n,
    ts,
    g.blobAmp * 0.7,
  );
  ctx.stroke();

  ctx.globalAlpha = 1;
  return g.a;
}
